/**
 * Multi-Step Equations generator.
 *
 * One-sided multi-step linear equations that require distribution and/or
 * combining like terms before the usual isolate-and-divide. Built via the
 * shared equation base: choose the solution first, assemble so it holds, then
 * validate independently.
 *
 * Families:
 *   M1: ax + b + cx + d = e        (combine like terms)
 *   M2: a(x + b) + c = d           (distribute then isolate)
 *   M3: a(x + b) + cx = d          (distribute + combine like terms)
 *   M4: a(bx + c) = d              (distribute a leading coefficient)
 */

import { Rational, rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearExpr, LinearEquation } from "../engine/linear";
import type { Difficulty, GenerateContext, Generator, Question } from "./types";
import {
  EQUATION_VARIABLES,
  generateEquation,
  equationSignature,
  coeffInt,
  type BuiltEquation,
  type EquationTopicOptions,
} from "./equation-base";
import { getDefaultCalibration } from "../curriculum/calibration";

const TOPIC = "multi-step-equations" as const;
const VERSION = "1.0.0";

type Family = "M1" | "M2" | "M3" | "M4";

interface Cfg {
  readonly families: readonly Family[];
  readonly cMin: number;
  readonly cMax: number;
  readonly kMin: number;
  readonly kMax: number;
  readonly solMin: number;
  readonly solMax: number;
  readonly pNeg: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["M1", "M2"], cMin: 1, cMax: 4, kMin: 1, kMax: 8, solMin: 1, solMax: 9, pNeg: 0.1 },
  standard: { families: ["M1", "M2", "M3", "M4"], cMin: 1, cMax: 6, kMin: 1, kMax: 10, solMin: -9, solMax: 9, pNeg: 0.4 },
  hard: { families: ["M2", "M3", "M4"], cMin: 2, cMax: 8, kMin: 1, kMax: 12, solMin: -12, solMax: 12, pNeg: 0.5 },
  challenge: { families: ["M3", "M4"], cMin: 2, cMax: 10, kMin: 1, kMax: 12, solMin: -12, solMax: 12, pNeg: 0.55 },
};

function factorLatex(c: Rational): string {
  if (c.equals(Rational.ONE)) return "";
  if (c.equals(Rational.of(-1n))) return "-";
  return c.toLatex();
}
function factorText(c: Rational): string {
  if (c.equals(Rational.ONE)) return "";
  if (c.equals(Rational.of(-1n))) return "-";
  return c.toString();
}
function coeffLatex(c: Rational, v: string): string {
  if (c.equals(Rational.ONE)) return v;
  if (c.equals(Rational.of(-1n))) return `-${v}`;
  return `${c.toLatex()}${v}`;
}
function coeffText(c: Rational, v: string): string {
  if (c.equals(Rational.ONE)) return v;
  if (c.equals(Rational.of(-1n))) return `-${v}`;
  return `${c.toString()}${v}`;
}
function plusL(r: Rational): string {
  return r.isNegative() ? `- ${r.neg().toLatex()}` : `+ ${r.toLatex()}`;
}
function plusT(r: Rational): string {
  return r.isNegative() ? `- ${r.neg().toString()}` : `+ ${r.toString()}`;
}

function build(rng: Rng, difficulty: Difficulty): BuiltEquation | null {
  const cfg = DIFF[difficulty];
  const v = rng.pick(EQUATION_VARIABLES);
  const family = rng.pick(cfg.families);
  const s = rat(rng.intNonZero(cfg.solMin, cfg.solMax));
  const c = () => coeffInt(rng, cfg.cMin, cfg.cMax, cfg.pNeg);
  const k = () => coeffInt(rng, cfg.kMin, cfg.kMax, cfg.pNeg);

  switch (family) {
    case "M1": {
      // ax + b + cx + d = e  ->  (a+c)x + (b+d) = e
      const a = c();
      let cc = c();
      if (a.add(cc).isZero()) cc = cc.add(rat(1)); // avoid vanishing variable
      const b = k();
      const d = k();
      const left = new LinearExpr(a.add(cc), b.add(d));
      const e = left.evaluate(s);
      const eq = new LinearEquation(left, LinearExpr.constantExpr(e));
      return {
        family,
        variable: v,
        displayLatex: `${coeffLatex(a, v)} ${plusL(b)} ${coeffLatex(cc, v)} ${plusL(d)} = ${e.toLatex()}`,
        displayText: `${coeffText(a, v)} ${plusT(b)} ${coeffText(cc, v)} ${plusT(d)} = ${e.toString()}`,
        equation: eq,
        solution: s,
        leadingStep: {
          description: "Combine like terms on the left side.",
          latex: `${coeffLatex(a.add(cc), v)} ${plusL(b.add(d))} = ${e.toLatex()}`,
        },
        leadingHints: ["Combine the variable terms and the constant terms first."],
      };
    }
    case "M2": {
      // a(x + b) + c = d
      const a = c();
      const b = k();
      const left = LinearExpr.variable().add(LinearExpr.constantExpr(b)).scale(a).add(LinearExpr.constantExpr(k()));
      const d = left.evaluate(s);
      const eq = new LinearEquation(left, LinearExpr.constantExpr(d));
      const cc = left.constant.sub(a.mul(b)); // recover the added constant for display
      return {
        family,
        variable: v,
        displayLatex: `${factorLatex(a)}(${v} ${plusL(b)}) ${plusL(cc)} = ${d.toLatex()}`,
        displayText: `${factorText(a)}(${v} ${plusT(b)}) ${plusT(cc)} = ${d.toString()}`,
        equation: eq,
        solution: s,
        leadingStep: { description: "Distribute, then combine constants.", latex: eqLatex(eq, v) },
        leadingHints: ["Distribute across the parentheses first."],
      };
    }
    case "M3": {
      // a(x + b) + cx = d
      const a = c();
      const b = k();
      let cc = c();
      if (a.add(cc).isZero()) cc = cc.add(rat(1));
      const left = LinearExpr.variable().add(LinearExpr.constantExpr(b)).scale(a).add(new LinearExpr(cc, rat(0)));
      const d = left.evaluate(s);
      const eq = new LinearEquation(left, LinearExpr.constantExpr(d));
      return {
        family,
        variable: v,
        displayLatex: `${factorLatex(a)}(${v} ${plusL(b)}) ${signCoeffL(cc, v)} = ${d.toLatex()}`,
        displayText: `${factorText(a)}(${v} ${plusT(b)}) ${signCoeffT(cc, v)} = ${d.toString()}`,
        equation: eq,
        solution: s,
        leadingStep: { description: "Distribute and combine like terms.", latex: eqLatex(eq, v) },
        leadingHints: ["Distribute, then combine the variable terms."],
      };
    }
    case "M4": {
      // a(bx + c) = d
      const a = c();
      const b = c();
      if (a.mul(b).isZero()) return null;
      const inner = k();
      const left = LinearExpr.variable().scale(b).add(LinearExpr.constantExpr(inner)).scale(a);
      const d = left.evaluate(s);
      const eq = new LinearEquation(left, LinearExpr.constantExpr(d));
      return {
        family,
        variable: v,
        displayLatex: `${factorLatex(a)}(${coeffLatex(b, v)} ${plusL(inner)}) = ${d.toLatex()}`,
        displayText: `${factorText(a)}(${coeffText(b, v)} ${plusT(inner)}) = ${d.toString()}`,
        equation: eq,
        solution: s,
        leadingStep: { description: "Distribute across the parentheses.", latex: eqLatex(eq, v) },
        leadingHints: ["Distribute the outside factor to both inside terms."],
      };
    }
  }
}

/** Signed coefficient term with a leading operator, e.g. "+ 3x" / "- x". */
function signCoeffL(c: Rational, v: string): string {
  if (c.isNegative()) return `- ${coeffLatex(c.neg(), v)}`;
  return `+ ${coeffLatex(c, v)}`;
}
function signCoeffT(c: Rational, v: string): string {
  if (c.isNegative()) return `- ${coeffText(c.neg(), v)}`;
  return `+ ${coeffText(c, v)}`;
}

function eqLatex(eq: LinearEquation, v: string): string {
  // Local minimal renderer for the simplified form used in leading steps.
  const l = eq.left;
  const parts: string[] = [];
  if (!l.coeff.isZero()) parts.push(coeffLatex(l.coeff, v));
  if (!l.constant.isZero())
    parts.push(parts.length === 0 ? l.constant.toLatex() : plusL(l.constant));
  const leftStr = parts.length === 0 ? "0" : parts.join(" ");
  return `${leftStr} = ${eq.right.constant.toLatex()}`;
}

function maxMagnitude(difficulty: Difficulty): bigint {
  const m: Record<Difficulty, number> = { easy: 80, standard: 140, hard: 260, challenge: 360 };
  return BigInt(m[difficulty]);
}

const OPTIONS: EquationTopicOptions = {
  topic: TOPIC,
  version: VERSION,
  maxMagnitude,
  requireIntegerSolution: (d) => d === "easy" || d === "standard",
  build,
};

// Calibration exists (getDefaultCalibration returns a profile) but this
// generator only produces integer solutions by difficulty config, matching the
// calibration's fractionalSolutions="never". Kept for future refinement.
void getDefaultCalibration(TOPIC);

export class MultiStepEquationsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];
  generate(ctx: GenerateContext): Question {
    return generateEquation(OPTIONS, ctx);
  }
  signature(q: Question) {
    return equationSignature(TOPIC, q);
  }
}

export const multiStepEquations = new MultiStepEquationsGenerator();
